import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { RootErrorBoundary, RouteErrorBoundary, InstallPrompt, DialogProvider, CookieConsent, Footer, PrivacyPolicy, TermsOfService } from "@bc/ui";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AdminLayout from "@/components/AdminLayout";
import "@/App.css";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const DashboardOverview = lazy(() => import("./pages/DashboardOverview"));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));
const Analytics = lazy(() => import("./pages/Analytics"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const VerificationPage = lazy(() => import("./pages/VerificationPage"));
const ConveyancersPage = lazy(() => import("./pages/ConveyancersPage"));
const BuyersPage = lazy(() => import("./pages/BuyersPage"));
const SellersPage = lazy(() => import("./pages/SellersPage"));
const SendEmailPage = lazy(() => import("./pages/SendEmailPage"));
const MarketingEmailPage = lazy(() => import("./pages/MarketingEmailPage"));
const FailedEmailsPage = lazy(() => import("./pages/FailedEmailsPage"));

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function withRouteBoundary(element) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
    </RouteErrorBoundary>
  );
}

// Public pages (login, password reset, legal) scroll the document with the
// footer in normal flow. Admin routes render the footer inside AdminLayout's
// fixed viewport frame instead. Reset the document scroll on navigation so
// every page opens at the top.
function PublicShell() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <Outlet />
      <Footer />
    </>
  );
}

// Mobile browsers aggressively restore scroll positions from history on
// back/forward navigation, which can override the per-route scroll resets.
// Take manual control so every page reliably opens at the top.
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

export default function App() {
  return (
    <RootErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <BrowserRouter>
          <AuthProvider>
          <DialogProvider>
            <Routes>
              {/* Public — footer flows after the page */}
              <Route element={<PublicShell />}>
                <Route path="/login" element={withRouteBoundary(<LoginPage />)} />
                <Route path="/forgot-password" element={withRouteBoundary(<ForgotPassword />)} />
                <Route path="/reset-password" element={withRouteBoundary(<ResetPassword />)} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
              </Route>
              <Route
                path="/dashboard"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <DashboardOverview />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/payments"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <PaymentsPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/analytics"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/verification"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <VerificationPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/users"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <UsersPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/conveyancers"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <ConveyancersPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/buyers"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <BuyersPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/sellers"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <SellersPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/send-email"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <SendEmailPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/marketing-email"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <MarketingEmailPage />
                  </ProtectedRoute>,
                )}
              />
              <Route
                path="/failed-emails"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <FailedEmailsPage />
                  </ProtectedRoute>,
                )}
              />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            <CookieConsent />
            <InstallPrompt />
          </DialogProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </RootErrorBoundary>
  );
}
